import i18n from '@breward/i18n';
import {
  ColorPaletteInput,
  GraphRatioSelect,
  LevelSelect,
  Select,
  TextArea,
  Toggle,
} from '@breward/ui';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import {
  ScoresheetSchemaField,
  ScoresheetSchemaSection,
  ScoresheetSchemaSubsection,
} from '../../../../types/scoresheetSchemas';
import DescriptorPreview from '../DescriptorPreview/DescriptorPreview';
import PaletteField from './components/PaletteField';
import SliderField from './components/SliderField';
import SelectField from './components/SelectField';
import TextareaField from './components/TextareaField';
import DescriptorField from './components/DescriptorField';
import GraphField from './components/GraphField';

interface ScoresheetFieldProps {
  values?: any;
  section: ScoresheetSchemaSection;
  subsection: ScoresheetSchemaSubsection;
  field: ScoresheetSchemaField;
}

const ScoresheetField = ({
  values,
  section,
  subsection,
  field,
}: ScoresheetFieldProps) => {
  const fieldSelector =
    values?.[camelCase(section.key)]?.[camelCase(subsection.key)]?.[
    camelCase(field.key)
    ];

  const formatLabels = (labels: any, key = 'labels') => {
    const normalize = (value: any) => {
      if (value === undefined || value === null) return value;
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') return value.split(',');
      return value;
    };

    const language = i18n.language || window.localStorage.i18nextLng;
    const translated = field?.settings?.translations?.[language]?.[key];

    if (translated !== undefined) {
      return normalize(translated);
    }

    return normalize(labels);
  };

  const renderField = () => {
    switch (field?.fieldType) {
      case 'textarea':
        return (
          <TextareaField label={field.label} value={fieldSelector} />
        );
      case 'slider':
        return (
          <SliderField
            label={field.label}
            description={field.description}
            value={fieldSelector}
            min={field?.settings?.minValue}
            max={field?.settings?.maxValue}
            step={field?.settings?.step}
            stepsLabels={formatLabels(field?.settings?.labels)}
          />
        );
      case 'select': {
        const selectOptions = formatLabels(field?.settings?.availableValues, 'available_values')?.map(
          (v: string) => ({ label: v, value: snakeCase(v) }),
        ) ?? [];
        return (
          <SelectField
            label={field.label}
            value={fieldSelector}
            multi={field?.settings?.multi}
            options={selectOptions}
          />
        );
      }
      case 'checkbox':
        return (
          <div className="my-1 flex h-8 flex-1 items-center justify-start px-1 md:my-2 md:px-2">
            <Toggle id={field?.key} value={fieldSelector} label={field.label} />
          </div>
        );
      case 'palette':
        return (
          <PaletteField value={fieldSelector} unit="ebc" />
        );
      case 'graph':
        return (
          <GraphField
            label={field.label}
            description={field.description}
            labels={formatLabels(field?.settings?.values, 'values')}
            values={fieldSelector || []}
          />
        );
      case 'descriptor':
        return (
          <DescriptorField
            withIntensity={field?.settings?.withIntensity}
            group={field?.settings?.descriptorsGroup}
            fieldSelector={fieldSelector}
          />
        );
      default:
        return <div></div>;
    }
  };

  return renderField();
};

export default ScoresheetField;
